//@flow
import React from 'react';
import {makeStyles, Typography} from '@material-ui/core';
import {PictureAsPdf} from '@material-ui/icons';
import {colors} from '../../assets/styles/colors';
import type {TemplateHeaderComponent as HeaderType} from '../../entities/multichat/whatsapp-api';

type TemplateVariable = {
  id: string,
  scope: 'HEADER' | 'BODY',
  name?: string,
  position?: number,
  value: string,
};

type TemplateMedia = {
  id: string,
  type: 'IMAGE' | 'VIDEO' | 'DOCUMENT',
  scope: 'HEADER',
  value: string,
};

type Props = {
  component: HeaderType,
  variables: TemplateVariable[],
  media: TemplateMedia[],
  showTemplate: boolean,
};

const useStyles = makeStyles(() => ({
  text: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.grey900,
  },
  image: {
    width: '100%',
    borderRadius: '8px',
  },
  documentContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  documentIcon: {
    color: colors.primary,
    fontSize: '24px',
  },
  documentText: {
    fontSize: '14px',
    color: colors.grey700,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
}));

const interpolateText = (
  templateText: string,
  variables: TemplateVariable[]
): React.Node => {
  if (!templateText) return templateText;

  const parts = templateText.split(/({{[\w-]+}})/);

  return parts.map((part, index) => {
    const match = part.match(/{{([\w-]+)}}/);

    if (match) {
      const variable = variables.find(v => v.name === match[1]);

      const value = variable?.value || part;

      return (
        <span
          key={index}
          style={{
            color: colors.primary,
            fontWeight: 'bold',
          }}
        >
          {value}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

export default function TemplateHeaderComponent({
  component,
  variables,
  media,
  showTemplate,
}: Props) {
  const classes = useStyles();

  if (component.format === 'IMAGE') {
    const headerMedia = media.find(m => m.scope === 'HEADER');
    const imageSrc = showTemplate
      ? component.example?.header_handle?.[0]
      : headerMedia?.value || component.example?.header_handle?.[0];

    if (imageSrc) {
      return <img src={imageSrc} alt="header" className={classes.image} />;
    }
  }

  if (component.format === 'VIDEO') {
    const headerMedia = media.find(m => m.scope === 'HEADER');
    const videoSrc = showTemplate
      ? component.example?.header_handle?.[0]
      : headerMedia?.value || component.example?.header_handle?.[0];

    if (videoSrc) {
      return <video src={videoSrc} controls className={classes.image} />;
    }
  }

  if (component.format === 'DOCUMENT') {
    const headerMedia = media.find(m => m.scope === 'HEADER');
    const documentSrc = showTemplate
      ? component.example?.header_handle?.[0]
      : headerMedia?.value || component.example?.header_handle?.[0];

    if (documentSrc) {
      // Extract filename from URL or use default
      const filename = documentSrc.split('/').pop() || 'Documento';
      const displayName = filename.includes('.') ? filename : 'Documento.pdf';
      return (
        <div className={classes.documentContainer}>
          <PictureAsPdf className={classes.documentIcon} />
          <Typography className={classes.documentText}>
            {displayName}
          </Typography>
        </div>
      );
    }
  }

  if (!component.text) return null;

  if (showTemplate) {
    return (
      <Typography className={classes.text}>
        {interpolateText(component.text, [])}
      </Typography>
    );
  }

  const headerVariables = variables.filter(v => v.scope === 'HEADER');
  return (
    <Typography className={classes.text}>
      {interpolateText(component.text, headerVariables)}
    </Typography>
  );
}
